import { signal } from '@angular/core';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { environment } from '../../../environments/environment';

export interface MallNotification {
  id: string;
  type: 'success' | 'info' | 'warning' | 'danger';
  message: string;
  title?: string;
  titleEn?: string;
  titleAr?: string;
  messageEn?: string;
  messageAr?: string;
  obligationType?: string;
  amount?: number;
  data?: Record<string, unknown>;
  timestamp: Date;
  read?: boolean;
  targetUrl?: string;
}

export interface PaymentCollectedEvent {
  paymentId: string;
  obligationType: string;
  amount: number;
  discountPct: number;
  tenantName?: string;
  shopNumber?: string;
  receiptNumber?: string;
  paidOn: string;
  mallId?: string;
  titleEn: string;
  titleAr: string;
  messageEn: string;
  messageAr: string;
}

export interface MeterReadingCapturedEvent {
  readingId: string;
  meterId: string;
  meterNumber?: string;
  location?: string;
  tenantName?: string;
  shopNumber?: string;
  readingValue: number;
  previousValue: number;
  consumptionKwh: number;
  readingDate: string;
  capturedAt: string;
  ocrConfidence?: number;
  wasManuallyOverridden: boolean;
  isOverwritten: boolean;
  titleEn?: string;
  titleAr?: string;
  messageEn?: string;
  messageAr?: string;
}

export interface ElectricityBillGeneratedEvent {
  billId: string;
  meterId: string;
  meterNumber?: string;
  billingMonth: number;
  billingYear: number;
  totalAmount: number;
  dueDate: string;
  titleEn?: string;
  titleAr?: string;
  messageEn?: string;
  messageAr?: string;
}

export interface RemittanceChangedEvent {
  remittanceId: string;
  remittanceNumber: string;
  action: string;
  status: string;
  collectorId: string;
  collectorName?: string;
  cashierId?: string;
  cashierName?: string;
  recipientName?: string;
  declaredAmount: number;
  verifiedAmount?: number;
  discrepancyAmount?: number;
  currency: string;
  mallId?: string;
  notes?: string;
  timestamp: string;
  titleEn: string;
  titleAr: string;
  messageEn: string;
  messageAr: string;
  collectorTitleEn?: string;
  collectorTitleAr?: string;
  collectorMessageEn?: string;
  collectorMessageAr?: string;
}

export const notifications = signal<MallNotification[]>([]);
export const notificationCount = signal(0);
export const latestMeterReadingEvent = signal<MeterReadingCapturedEvent | null>(null);
export const latestPaymentCollectedEvent = signal<PaymentCollectedEvent | null>(null);
export const latestRemittanceChangedEvent = signal<RemittanceChangedEvent | null>(null);

let connection: HubConnection | null = null;
let currentTokenFactory: () => string | null = () => {
  return localStorage.getItem('access_token');
};

export function connectNotificationHub(
  tokenOrFactory?: string | (() => string | null),
  hubBaseUrl?: string
): HubConnection {
  if (typeof tokenOrFactory === 'function') {
    currentTokenFactory = tokenOrFactory;
  } else if (typeof tokenOrFactory === 'string' && tokenOrFactory.length > 0) {
    const staticToken = tokenOrFactory;
    currentTokenFactory = () => staticToken;
  }

  if (connection) {
    if (connection.state === 'Disconnected') {
      connection.start().catch((err) => console.error('SignalR reconnection failed', err));
    }
    return connection;
  }

  const baseUrl = hubBaseUrl || (typeof window !== 'undefined' && window.location.hostname !== 'localhost' ? `${window.location.protocol}//api.${window.location.host.replace(/^[^.]+\./, '')}` : environment.apis.default.url) || environment.apis.default.url;
  connection = new HubConnectionBuilder()
    .withUrl(`${baseUrl}/signalr-hubs/mall-management`, {
      accessTokenFactory: () => currentTokenFactory() ?? localStorage.getItem('access_token') ?? '',
    })
    .withAutomaticReconnect()
    .build();

  connection.on('PaymentConfirmed', (data: unknown) => {
    const d = data as Record<string, unknown>;
    const titleEn = (d?.['titleEn'] as string) || 'Payment Confirmed';
    const titleAr = (d?.['titleAr'] as string) || 'تم تأكيد الدفعة';
    const messageEn = (d?.['messageEn'] as string) || 'Payment has been successfully confirmed';
    const messageAr = (d?.['messageAr'] as string) || 'تم تأكيد استلام الدفعة بنجاح';
    pushNotification('success', messageEn, data, titleEn, titleAr, messageEn, messageAr, 'Rent');
  });

  connection.on('PaymentCollected', (data: unknown) => {
    const pc = data as PaymentCollectedEvent;
    latestPaymentCollectedEvent.set({ ...pc, _receivedAt: Date.now() } as PaymentCollectedEvent & { _receivedAt: number });
    pushNotification(
      'success',
      pc.messageEn || 'Payment Collected',
      data,
      pc.titleEn,
      pc.titleAr,
      pc.messageEn,
      pc.messageAr,
      pc.obligationType,
      pc.amount
    );
  });

  connection.on('RentOverdue', (data: unknown) => {
    const d = data as Record<string, unknown>;
    const titleEn = (d?.['titleEn'] as string) || 'Rent Overdue';
    const titleAr = (d?.['titleAr'] as string) || 'إيجار متأخر السداد';
    const messageEn = (d?.['messageEn'] as string) || 'A rental obligation is overdue for collection';
    const messageAr = (d?.['messageAr'] as string) || 'يوجد استحقاق إيجار متأخر السداد';
    pushNotification('warning', messageEn, data, titleEn, titleAr, messageEn, messageAr, 'Rent');
  });

  connection.on('ElectricityBillGenerated', (data: unknown) => {
    const bill = data as ElectricityBillGeneratedEvent;
    const period = formatBillingPeriod(bill.billingMonth, bill.billingYear);
    const meter = bill.meterNumber ? ` #${bill.meterNumber}` : '';
    pushNotification(
      'info',
      bill.messageEn || `Electricity bill for ${period.en} generated${meter}`,
      data,
      bill.titleEn || 'Electricity Bill Generated',
      bill.titleAr || 'تم إصدار فاتورة كهرباء',
      bill.messageEn || `Electricity bill for ${period.en} generated${meter}`,
      bill.messageAr || `تم إصدار فاتورة الكهرباء لشهر ${period.ar}${meter}`,
      'Electricity',
      bill.totalAmount,
      '/electricity/bills'
    );
  });

  connection.on('MeterReadingCaptured', (data: unknown) => {
    const reading = data as MeterReadingCapturedEvent;
    latestMeterReadingEvent.set(reading);

    const meterNum = reading.meterNumber ? ` #${reading.meterNumber}` : '';
    const readingVal = reading.readingValue != null ? reading.readingValue : '';
    const kwh = reading.consumptionKwh != null && reading.consumptionKwh > 0 ? ` (استهلاك: ${reading.consumptionKwh} ك.و.س)` : '';
    const kwhEn = reading.consumptionKwh != null && reading.consumptionKwh > 0 ? ` (Consumption: ${reading.consumptionKwh} kWh)` : '';

    const titleEn = reading.titleEn || 'Meter Reading Recorded';
    const titleAr = reading.titleAr || 'تم تسجيل قراءة عداد';
    const messageEn = reading.messageEn || `Meter${meterNum} reading recorded: ${readingVal} kWh${kwhEn}`;
    const messageAr = reading.messageAr || `تم تسجيل قراءة العداد${meterNum}: ${readingVal} ك.و.س${kwh}`;

    pushNotification(
      'info',
      messageEn,
      data,
      titleEn,
      titleAr,
      messageEn,
      messageAr,
      'MeterReading',
      undefined,
      '/electricity/readings'
    );
  });

  connection.on('RemittanceChanged', (data: unknown) => {
    const rem = data as RemittanceChangedEvent;
    latestRemittanceChangedEvent.set({ ...rem, _receivedAt: Date.now() } as RemittanceChangedEvent & { _receivedAt: number });

    let currentUserId: string | null = null;
    try {
      const userRaw = localStorage.getItem('currentUser');
      if (userRaw) {
        const parsed = JSON.parse(userRaw);
        currentUserId = parsed.id || parsed.sub || null;
      }
    } catch {}

    if (rem.action === 'Submitted' && currentUserId && rem.collectorId && rem.collectorId.toLowerCase() === currentUserId.toLowerCase()) {
      return;
    }

    const notifType: MallNotification['type'] =
      rem.action === 'Accepted' ? 'success' :
      rem.action === 'Rejected' ? 'danger' :
      rem.action === 'Returned' ? 'warning' : 'info';

    pushNotification(
      notifType,
      rem.messageEn || 'Remittance Updated',
      data,
      rem.titleEn,
      rem.titleAr,
      rem.messageEn,
      rem.messageAr,
      'CashRemittance',
      rem.verifiedAmount ?? rem.declaredAmount,
      '/cash-remittances'
    );
  });

  connection.start().catch((err) => console.error('SignalR connection failed', err));
  return connection;
}


export function disconnectNotificationHub(): Promise<void> {
  if (!connection) return Promise.resolve();
  return connection.stop().finally(() => {
    connection = null;
  });
}

function pushNotification(
  type: MallNotification['type'],
  message: string,
  data?: unknown,
  titleEn?: string,
  titleAr?: string,
  messageEn?: string,
  messageAr?: string,
  obligationType?: string,
  amount?: number,
  targetUrl?: string
) {
  const dataObj = data as Record<string, unknown> | undefined;
  const paymentId = dataObj?.['paymentId'] as string | undefined;
  const billId = dataObj?.['billId'] as string | undefined;

  // Deduplication: prevent duplicate notifications from duplicate events
  const existing = notifications();
  if (paymentId && existing.some((n) => (n.data as Record<string, unknown> | undefined)?.['paymentId'] === paymentId)) {
    return;
  }
  if (billId && existing.some((n) => (n.data as Record<string, unknown> | undefined)?.['billId'] === billId)) {
    return;
  }
  const now = Date.now();
  if (
    existing.some(
      (n) =>
        (n.message === message || (Boolean(messageAr) && n.messageAr === messageAr)) &&
        Math.abs(now - new Date(n.timestamp).getTime()) < 4000
    )
  ) {
    return;
  }

  const item: MallNotification = {
    id: crypto.randomUUID(),
    type,
    message,
    title: titleEn || message,
    titleEn: titleEn || message,
    titleAr: titleAr || message,
    messageEn: messageEn || message,
    messageAr: messageAr || message,
    obligationType,
    amount,
    data: data as Record<string, unknown>,
    timestamp: new Date(),
    read: false,
    targetUrl,
  };
  notifications.update((items) => [item, ...items.slice(0, 99)]);
  notificationCount.update((count) => count + 1);
}

export function clearNotifications(): void {
  notifications.set([]);
  notificationCount.set(0);
}

const AR_MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

function formatBillingPeriod(month: number, year: number): { en: string; ar: string } {
  const fallback = `${String(month).padStart(2, '0')}/${year}`;
  if (!month || month < 1 || month > 12) {
    return { en: fallback, ar: fallback };
  }
  try {
    const en = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
    return { en, ar: `${AR_MONTHS[month - 1]} ${year}` };
  } catch {
    return { en: fallback, ar: `${AR_MONTHS[month - 1]} ${year}` };
  }
}
